import '@mdi/font/css/materialdesignicons.min.css'
import 'viewerjs/dist/viewer.css'

import Vue from 'vue'
import VueRouter from 'vue-router'
import VueSanitize from 'vue-sanitize'
import Viewer from 'v-viewer'
import VueSnip from 'vue-snip'
import VTooltip from 'v-tooltip'
import TextHighlight from 'vue-text-highlight'
import VuetifyMask from 'vuetify-mask'
import moment from 'moment'

import { stopTokensRefreshingProcess } from '@/lib/Azure'
import { fetchAccountInfo } from '@/lib/appBootstrap'
import Tracking from '@/plugins/Tracking.js'
import FeatureFlags from '@/plugins/FeatureFlags.js'
import vuetify from '@/plugins/vuetify'
import disableNumberScrollDirective from '@/directives/disableNumberScroll'
import infiniteYScrollDirective from '@/directives/infiniteYScroll'
import linkify from '@/directives/linkify'

import log from '@/lib/log.js'
import access from '@/lib/access'
import { createOrgSwitchNavigationGuard } from '@/lib/orgSwitchGuard'

import App from './App/App.vue'
import routes from './routes'
import store from './store'
import i18n from './locales'
import constants from './constants'
import config from './config'

import styleVariables from '@/styles/main.scss'

let app

async function init() {
  await fetchAccountInfo(store)
  setupLanguage()

  setupPluginsAndDirectives()
  setupAppConfigAndGlobalProps()

  const router = setupRouter()
  mountApp(router)
}

function setupPluginsAndDirectives() {
  Vue.use(VueRouter)
  // The Tracking plugin should be placed after the FeatureFlags plugin since it checks a flag to determine wich support chat provider should be used durint its initialization
  Vue.use(FeatureFlags)
  Vue.use(Tracking)
  Vue.use(Viewer)
  Vue.use(VueSnip)
  Vue.use(VuetifyMask)
  Vue.use(VueSanitize, {
    allowedTags: ['strong', 'p', 'h1', 'br', 'em', 'a'],
    enforceHtmlBoundary: true,
    allowedSchemes: ['http', 'https'],
    allowedSchemesByTag: {
      img: ['data'],
    },
  })

  Vue.use(VTooltip, {
    popover: {
      defaultContainer: '[data-app]',
    },
  })
  Vue.component('text-highlight', TextHighlight)
  Vue.directive('disable-number-scroll', disableNumberScrollDirective)
  Vue.directive('linkify', linkify)
  Vue.directive('infinite-y-scroll', infiniteYScrollDirective)
  Vue.mixin({ methods: { log } })
}

function setupAppConfigAndGlobalProps() {
  Vue.config.silent = config.env === 'production'
  Vue.prototype.$constants = constants
  Vue.prototype.$styleVariables = Object.freeze(styleVariables)
}

function setupLanguage() {
  const language = store.getters.language
  i18n.i18next.changeLanguage(language)
  let safeLanguage = language
  // moment doesn't support 'zh' without a dialect and doesn't support 'zu' so we added a fallback to english instead.
  if (language.includes('zh')) safeLanguage = 'zh-cn'
  if (language.includes('zu')) safeLanguage = 'en'

  moment.locale(safeLanguage)
}

function setupRouter() {
  const router = new VueRouter({ base: '/', mode: 'history', routes })
  globalThis.$router = router

  router.beforeEach(createOrgSwitchNavigationGuard(store))

  router.beforeEach((to, _from, next) => {
    setDocumentTitle(to.meta)

    if (to.name !== 'logout' && !organizationPlanStillValid()) {
      return next('/logout')
    }

    if (hasAccessToRoute(to.meta)) next()
    else next('/404')
  })

  return router
}

function setDocumentTitle(routeMetadata) {
  document.title = routeMetadata.titleKey
    ? `Rever - ${i18n.t(routeMetadata.titleKey)}`
    : 'Rever'
}

function organizationPlanStillValid() {
  const organization = store.getters.organization

  if (organization.plan !== 'essentials') return true

  const freeTrialEndDate =
    new Date(organization.createdAt).getTime() +
    constants.freeTrialDurationInMilliseconds
  const currentDate = new Date().getTime()

  return currentDate < freeTrialEndDate
}

function hasAccessToRoute(routeMetadata) {
  const isLoggedIn = Boolean(store.getters.token)
  const organization = store.getters.organization

  if (!isLoggedIn || !routeMetadata.isProtected) return true

  if (access.aclsAllowedByPlan(organization, routeMetadata.acl)) return true
  return false
}

function mountApp(router) {
  app = new Vue({
    router,
    i18n,
    store,
    vuetify,
    render: h => h(App),
  })

  app.$mount('#app')
}

init()

if (module.hot) {
  module.hot.dispose(() => {
    stopTokensRefreshingProcess()

    if (app) {
      app.$destroy()
      app = null
    }

    globalThis.$router = null
  })
}
